import { Component, OnInit, OnDestroy, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';

declare var AOS: any;

@Component({
  selector: 'app-landing',
  standalone: true,
  imports: [CommonModule, RouterLink],
  template: `
    <div class="landing-font min-h-screen flex flex-col justify-between overflow-x-hidden bg-slate-50 text-slate-800">
      
      <!-- HEADER -->
      <header class="header-bg relative h-32 md:h-36 flex items-center justify-between border-b border-sky-100 shadow-sm overflow-hidden">
        <!-- Live Data Interoperability Particles Background -->
        <canvas id="headerParticles" class="absolute inset-0 z-0 pointer-events-none opacity-50"></canvas>
        <div class="absolute inset-0 bg-sky-900/10 pointer-events-none"></div>
        <div class="w-full max-w-7xl mx-auto px-6 sm:px-12 flex flex-col md:flex-row items-center justify-between gap-4 z-10">
          <div class="flex items-center gap-3">
            <img src="/img/smz.png" class="w-10 sm:w-16 h-auto" alt="SMZ Government Emblem" />
            <div class="text-left">
              <span class="text-xs uppercase tracking-widest font-black text-slate-500 block">Zanzibar Portal</span>
              <span class="text-sm sm:text-base md:text-lg font-extrabold text-slate-900">Revolution Government of Zanzibar</span>
            </div>
          </div>

          <div class="flex items-center gap-3 sm:gap-5 text-xs sm:text-sm font-bold text-slate-650">
            <a routerLink="/" class="hover:text-emerald-700 transition-colors">Home</a>
            <a routerLink="/onboarding" class="hover:text-emerald-700 transition-colors hidden sm:inline">Register Institution</a>
            <a routerLink="/onboarding" [queryParams]="{ view: 'lookup' }" class="hover:text-emerald-700 transition-colors inline-flex items-center gap-1">
              <svg class="w-3.5 h-3.5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <span>Track Status</span>
            </a>
            <a routerLink="/login" class="bg-emerald-600 hover:bg-emerald-500 px-5 py-2 rounded-full text-white font-extrabold transition-all shadow-md shadow-emerald-600/20">
              Login
            </a>
          </div>
        </div>
      </header>

      <!-- HERO SLIDER SECTION -->
      <section class="relative min-h-[38rem] sm:min-h-[42rem] md:min-h-[46rem] py-16 overflow-hidden bg-slate-950 flex items-center justify-center">
        <!-- Carousel Background Images with enhanced visibility -->
        <div class="absolute inset-0 z-0">
          <div
            *ngFor="let slide of slides; let i = index"
            class="absolute inset-0 bg-cover bg-center transition-all duration-1000 ease-in-out"
            [style.backgroundImage]="'url(/img/hero-slides/' + slide + ')'"
            [class.opacity-100]="activeSlide() === i"
            [class.scale-100]="activeSlide() === i"
            [class.opacity-0]="activeSlide() !== i"
            [class.scale-105]="activeSlide() !== i"
          ></div>
          <!-- Subtle vignette gradient overlay that keeps background photo bright and vivid -->
          <div class="absolute inset-0 bg-gradient-to-b from-slate-950/60 via-slate-950/25 to-slate-950/70 pointer-events-none"></div>
        </div>

        <!-- Carousel Navigation Arrows -->
        <button
          (click)="prevSlide()"
          class="absolute left-3 sm:left-8 z-20 p-2.5 sm:p-3 rounded-full bg-black/35 hover:bg-black/65 text-white/80 hover:text-white backdrop-blur-md border border-white/20 transition-all hover:scale-110 shadow-lg cursor-pointer"
          aria-label="Previous slide"
        >
          <svg class="w-5 h-5 sm:w-6 sm:h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <button
          (click)="nextSlide()"
          class="absolute right-3 sm:right-8 z-20 p-2.5 sm:p-3 rounded-full bg-black/35 hover:bg-black/65 text-white/80 hover:text-white backdrop-blur-md border border-white/20 transition-all hover:scale-110 shadow-lg cursor-pointer"
          aria-label="Next slide"
        >
          <svg class="w-5 h-5 sm:w-6 sm:h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M9 5l7 7-7 7" />
          </svg>
        </button>

        <!-- Hero Content Overlay with Glassmorphic Card for pristine legibility -->
        <div class="relative z-10 text-center max-w-4xl mx-4 sm:mx-16 md:mx-auto px-6 sm:px-12 py-8 sm:py-10 rounded-2xl sm:rounded-3xl bg-slate-950/35 backdrop-blur-md border border-white/20 shadow-2xl space-y-6 sm:space-y-7 flex flex-col items-center">
          <h1
            data-aos="fade-right"
            data-aos-duration="1000"
            class="text-4xl sm:text-6xl md:text-7xl font-black text-emerald-400 tracking-tight drop-shadow-[0_4px_12px_rgba(0,0,0,0.8)]"
          >
            Zan X-Change
          </h1>
          
          <div class="text-[1rem] md:text-[1.15rem] md:mx-[30px] font-medium text-white">
            <p
              data-aos="fade-left"
              data-aos-delay="200"
              class="max-w-[95%] mx-auto leading-relaxed text-center text-slate-100 drop-shadow-md"
            >
              A data sharing and exchange platform bolstered by consent mechanisms, where data owners have a say on how, where, and when their data is moved around. It has inbuilt tech and governance attributes including data privacy, accountability and oversight mechanisms, that benefits both citizens and service providers.
            </p>
          </div>

          <div class="pt-2 flex justify-center">
            <a
              routerLink="/login"
              [queryParams]="{ register: 'true' }"
              class="bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-sm sm:text-base md:text-lg uppercase px-8 sm:px-16 rounded-full py-3.5 shadow-xl shadow-emerald-600/40 hover:scale-105 transition-all flex gap-3 items-center"
            >
              Register Now
              <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </a>
          </div>
        </div>

        <!-- Slide Indicators -->
        <div class="absolute bottom-5 left-0 right-0 flex justify-center items-center gap-2.5 z-20">
          <button
            *ngFor="let slide of slides; let i = index"
            (click)="setSlide(i)"
            class="h-2.5 rounded-full transition-all duration-300 border border-white/60 cursor-pointer shadow-md"
            [class.w-8]="activeSlide() === i"
            [class.bg-emerald-400]="activeSlide() === i"
            [class.w-2.5]="activeSlide() !== i"
            [class.bg-white/40]="activeSlide() !== i"
            [attr.aria-label]="'Go to slide ' + (i + 1)"
          ></button>
        </div>
      </section>

      <!-- WRAPPER 1: SERVICES & WHY ZAN (BG: bg.png) -->
      <div class="bg-[url('/img/bg.png')] bg-contain bg-repeat">
        
        <!-- CORE SERVICES SECTION -->
        <section class="py-16 sm:py-24 px-6 sm:px-12 lg:px-40 xl:px-48">
          <div class="max-w-7xl mx-auto space-y-16">
            <div class="flex flex-col items-center">
              <h2 class="text-3xl text-[#30476f] font-black uppercase">Core Services</h2>
              <div class="section-header-line mt-2"></div>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-3 gap-8 pt-8">
              <!-- Service 1: Data Exchange -->
              <div
                data-aos="fade-down"
                data-aos-duration="600"
                class="flex flex-col items-center bg-primary py-16 px-10 rounded-xl text-white text-center hover:scale-105 hover:shadow-lg transition-all duration-300 relative mt-5 md:mt-14"
              >
                <div class="w-[90px] h-[90px] rounded-full bg-blue-100 text-[#1399d1] flex justify-center items-center shadow absolute -top-10">
                  <svg class="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                  </svg>
                </div>
                <h3 class="text-2xl font-bold mb-5 mt-6">Data Exchange</h3>
                <p class="text-lg leading-relaxed">
                  Provides a robust, standardized digital highway enabling public and private entities to exchange mission-critical data in real time.
                </p>
                <!-- <button class="mt-10 px-5 py-2 bg-slate-100 text-primary font-bold rounded-full text-sm hover:bg-slate-200 transition-colors">
                  Read More
                </button> -->
              </div>

              <!-- Service 2: Data Sharing -->
              <div
                data-aos="fade-down"
                data-aos-duration="600"
                class="flex flex-col items-center bg-primary py-16 px-10 rounded-xl text-white text-center hover:scale-105 hover:shadow-lg transition-all duration-300 relative mt-5 md:mt-14"
              >
                <div class="w-[90px] h-[90px] rounded-full bg-blue-100 text-[#1399d1] flex justify-center items-center shadow absolute -top-10">
                  <svg class="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.684 10.742l5.485-2.742m0 0a3 3 0 101.378-2.517l-5.485 2.742m0 0a3 3 0 101.378 2.517L14.24 16.258m0 0a3 3 0 101.378 2.517" />
                  </svg>
                </div>
                <h3 class="text-2xl font-bold mb-5 mt-6">Data Sharing</h3>
                <p class="text-lg leading-relaxed">
                  Facilitates governed data sharing requests (DSR) with structured multi-tier approval workflows, role-based oversight, and institutional SLAs.
                </p>
                <!-- <button class="mt-10 px-5 py-2 bg-slate-100 text-primary font-bold rounded-full text-sm hover:bg-slate-200 transition-colors">
                  Read More
                </button> -->
              </div>

              <!-- Service 3: Data Security -->
              <div
                data-aos="fade-down"
                data-aos-duration="600"
                class="flex flex-col items-center bg-primary py-16 px-10 rounded-xl text-white text-center hover:scale-105 hover:shadow-lg transition-all duration-300 relative mt-5 md:mt-14"
              >
                <div class="w-[90px] h-[90px] rounded-full bg-blue-100 text-[#1399d1] flex justify-center items-center shadow absolute -top-10">
                  <svg class="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <h3 class="text-2xl font-bold mb-5 mt-6">Data Security</h3>
                <p class="text-lg leading-relaxed">
                  Enforces zero-trust defense with end-to-end payload encryption, digital certificate validation, and comprehensive access control policies.
                </p>
                <!-- <button class="mt-10 px-5 py-2 bg-slate-100 text-primary font-bold rounded-full text-sm hover:bg-slate-200 transition-colors">
                  Read More
                </button> -->
              </div>
            </div>

            <!-- JAMII X-CHANGE FEDERATION FEATURE CARD -->
            <div
              data-aos="fade-up"
              data-aos-duration="600"
              class="col-span-1 md:col-span-3 flex flex-col md:flex-row bg-white items-center border-2 border-primary rounded-lg p-8 md:p-16 hover:scale-105 duration-300 transition-all ease-in-out mt-12"
            >
              <div class="w-full md:w-6/12 flex md:block justify-center mb-5">
                <img src="/img/jamii.png" class="w-[130px] md:w-[300px]" alt="Jamii logo" />
              </div>
              <div class="flex flex-col items-center text-center md:text-left md:block mt-10 md:mt-0">
                <p class="text-2xl md:text-4xl font-bold mb-6 text-primary">
                  Jamii X-Change Federation
                </p>
                <p class="leading-relaxed text-lg">
                  Jamii X-change provides smooth and efficient transfer of data between systems, applications, or organizations.
                </p>
                <button class="mt-10 px-5 py-2 text-white rounded-full bg-primary font-bold hover:opacity-90">
                  Read More
                </button>
              </div>
            </div>
          </div>
        </section>

        <!-- WHY ZAN X-CHANGE SECTION -->
        <section class="py-16 sm:py-24 px-6 sm:px-12 lg:px-40 xl:px-48">
          <div class="max-w-7xl mx-auto space-y-16">
            <div class="flex flex-col items-center">
              <h2 class="text-3xl text-[#30476f] font-black uppercase">Why Zan X-Change</h2>
              <div class="section-header-line mt-2"></div>
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              <!-- Reason 1: Secure -->
              <div
                data-aos="fade-up"
                data-aos-duration="600"
                class="group p-5 lg:p-14 pt-6 shadow-lg border-2 border-primary flex flex-col items-center rounded-lg bg-white hover:bg-primary hover:text-white duration-300 transition-all ease-in-out hover:scale-105 text-center"
              >
                <div class="mb-10 text-primary group-hover:text-white duration-300 transition-all ease-in-out">
                  <svg class="w-16 h-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <p class="text-2xl font-bold mb-5">Secure</p>
                <p class="text-center leading-relaxed text-lg text-slate-650 group-hover:text-white">
                  Protects sensitive records with mutual TLS encryption, digital certificates, and hardened security servers across every interconnected node.
                </p>
              </div>

              <!-- Reason 2: Efficiency -->
              <div
                data-aos="fade-up"
                data-aos-duration="600"
                class="group p-5 lg:p-14 pt-6 shadow-lg border-2 border-primary flex flex-col items-center rounded-lg bg-white hover:bg-primary hover:text-white duration-300 transition-all ease-in-out hover:scale-105 text-center"
              >
                <div class="mb-10 text-primary group-hover:text-white duration-300 transition-all ease-in-out">
                  <svg class="w-16 h-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <p class="text-2xl font-bold mb-5">Efficiency</p>
                <p class="text-center leading-relaxed text-lg text-slate-650 group-hover:text-white">
                  Accelerates government workflows and eliminates paper-based delays through automated, real-time data exchange and instant API sync.
                </p>
              </div>

              <!-- Reason 3: Transparency -->
              <div
                data-aos="fade-up"
                data-aos-duration="600"
                class="group p-5 lg:p-14 pt-6 shadow-lg border-2 border-primary flex flex-col items-center rounded-lg bg-white hover:bg-primary hover:text-white duration-300 transition-all ease-in-out hover:scale-105 text-center"
              >
                <div class="mb-10 text-primary group-hover:text-white duration-300 transition-all ease-in-out">
                  <svg class="w-16 h-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </div>
                <p class="text-2xl font-bold mb-5">Transparency</p>
                <p class="text-center leading-relaxed text-lg text-slate-655 group-hover:text-white">
                  Delivers end-to-end accountability through tamper-evident audit trails, live status dashboards, and clear institutional provenance.
                </p>
              </div>

              <!-- Reason 4: Integrity -->
              <div
                data-aos="fade-up"
                data-aos-duration="600"
                class="group p-5 lg:p-14 pt-6 shadow-lg border-2 border-primary flex flex-col items-center rounded-lg bg-white hover:bg-primary hover:text-white duration-300 transition-all ease-in-out hover:scale-105 text-center"
              >
                <div class="mb-10 text-primary group-hover:text-white duration-300 transition-all ease-in-out">
                  <svg class="w-16 h-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                  </svg>
                </div>
                <p class="text-2xl font-bold mb-5">Integrity</p>
                <p class="text-center leading-relaxed text-lg text-slate-655 group-hover:text-white">
                  Guarantees that shared datasets remain authentic, unaltered, and digitally signed with strict non-repudiation mechanisms.
                </p>
              </div>

              <!-- Reason 5: Privacy -->
              <div
                data-aos="fade-up"
                data-aos-duration="600"
                class="group p-5 lg:p-14 pt-6 shadow-lg border-2 border-primary flex flex-col items-center rounded-lg bg-white hover:bg-primary hover:text-white duration-300 transition-all ease-in-out hover:scale-105 text-center"
              >
                <div class="mb-10 text-primary group-hover:text-white duration-300 transition-all ease-in-out">
                  <svg class="w-16 h-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <p class="text-2xl font-bold mb-5">Privacy</p>
                <p class="text-center leading-relaxed text-lg text-slate-655 group-hover:text-white">
                  Puts citizens in control with granular consent management and purpose-bound access in compliance with ZPDPC data regulations.
                </p>
              </div>

              <!-- Reason 6: Interoperability -->
              <div
                data-aos="fade-up"
                data-aos-duration="600"
                class="group p-5 lg:p-14 pt-6 shadow-lg border-2 border-primary flex flex-col items-center rounded-lg bg-white hover:bg-primary hover:text-white duration-300 transition-all ease-in-out hover:scale-105 text-center"
              >
                <div class="mb-10 text-primary group-hover:text-white duration-300 transition-all ease-in-out">
                  <svg class="w-16 h-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                </div>
                <p class="text-2xl font-bold mb-5">Interoperability</p>
                <p class="text-center leading-relaxed text-lg text-slate-655 group-hover:text-white">
                  Unifies disparate government MDAs, municipalities, and private platforms into a single ecosystem via standardized X-Road protocols.
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>

      <!-- WRAPPER 2: STAKEHOLDERS & FOOTER (BG: zanzibar.jpg) -->
      <div class="bg-[url('/img/zanzibar.jpg')] bg-cover bg-center">
        
        <!-- STAKEHOLDERS SECTION -->
        <section class="py-16 px-6 sm:px-12 bg-sky-900 bg-opacity-65 text-white text-center">
          <div class="max-w-7xl mx-auto space-y-12">
            <div class="flex flex-col items-center">
              <h2 class="text-3xl text-white font-black tracking-wide uppercase">Stakeholders</h2>
              <div class="section-header-line mt-2 bg-white"></div>
            </div>

            <div
              data-aos="fade-down-left"
              data-aos-duration="500"
              class="flex justify-center items-center"
            >
              <img src="/img/stakeholder.png" class="md:w-[50%]" alt="Stakeholders diagram" />
            </div>
          </div>
        </section>

        <!-- FOOTER -->
        <footer class="footer bg-sky-850 bg-opacity-70 text-white py-16 px-6 sm:px-12 border-t border-sky-850">
          <div class="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-12 border-b border-sky-850 pb-12">
            <div class="space-y-4">
              <img src="/img/logo-w.png" class="w-32 h-auto" alt="Zan X-Change Logo" />
              <p class="text-slate-200 text-sm leading-relaxed">
                Designed to facilitate secure and efficient data sharing between Government and private Institutions.
              </p>
              <div class="mb-10">
                <p class="mb-4 text-lg">Follow us on</p>
                <div class="flex gap-2">
                  <a href="#" class="w-10 h-10 rounded-full bg-slate-100 text-sky-900 flex items-center justify-center font-bold text-lg hover:bg-sky-100 transition-colors">F</a>
                  <a href="#" class="w-10 h-10 rounded-full bg-slate-100 text-sky-900 flex items-center justify-center font-bold text-lg hover:bg-sky-100 transition-colors">T</a>
                  <a href="#" class="w-10 h-10 rounded-full bg-slate-100 text-sky-900 flex items-center justify-center font-bold text-lg hover:bg-sky-100 transition-colors">Y</a>
                  <a href="#" class="w-10 h-10 rounded-full bg-slate-100 text-sky-900 flex items-center justify-center font-bold text-lg hover:bg-sky-100 transition-colors">I</a>
                </div>
              </div>
            </div>

            <div class="space-y-4">
              <h4 class="text-xl font-bold">Quick Links</h4>
              <div class="flex flex-col gap-4 mt-5 text-slate-200 text-sm">
                <a href="#" class="hover:text-white transition-colors">Home</a>
                <a href="#" class="hover:text-white transition-colors">About Us</a>
                <a href="#" class="hover:text-white transition-colors">Privacy & Policy</a>
                <a href="#" class="hover:text-white transition-colors">FAQ's</a>
              </div>
            </div>

            <div class="space-y-4">
              <h4 class="text-xl font-bold">Related Links</h4>
              <div class="flex flex-col gap-4 mt-5 text-slate-200 text-sm">
                <a href="#" class="hover:text-white transition-colors">MOICT Zanzibar</a>
                <a href="#" class="hover:text-white transition-colors">eGaz Authority</a>
                <a href="#" class="hover:text-white transition-colors">ZICTIA</a>
                <a href="#" class="hover:text-white transition-colors">PDPC Commission</a>
              </div>
            </div>

            <div class="space-y-4">
              <h4 class="text-xl font-bold">Contacts</h4>
              <div class="flex flex-col gap-4 mt-5 text-slate-200 text-sm">
                <p>Zanzibar, Tanzania</p>
                <p>(+255) 000 000 000</p>
                <p>info&#64;mail.go.tz</p>
                <p>contact&#64;mail.go.tz</p>
              </div>
            </div>
          </div>

          <p class="text-center text-xs text-slate-300 mt-8">
            Copyright &copy; {{ currentYear }} Zanzibar e-Government Authority. All Rights Reserved | Zan X-Change Version 1.0
          </p>
        </footer>
      </div>
    </div>
  `
})
export class LandingComponent implements OnInit, OnDestroy {
  slides = ['1.JPG', '2.jpg', '3.jpg'];
  activeSlide = signal<number>(0);
  currentYear = new Date().getFullYear();
  slideIntervalId: any;

  startSlideTimer() {
    if (this.slideIntervalId) clearInterval(this.slideIntervalId);
    this.slideIntervalId = setInterval(() => {
      this.activeSlide.update(idx => (idx + 1) % this.slides.length);
    }, 6000);
  }

  nextSlide() {
    this.activeSlide.update(idx => (idx + 1) % this.slides.length);
    this.startSlideTimer();
  }

  prevSlide() {
    this.activeSlide.update(idx => (idx - 1 + this.slides.length) % this.slides.length);
    this.startSlideTimer();
  }

  setSlide(index: number) {
    this.activeSlide.set(index);
    this.startSlideTimer();
  }

  ngOnInit() {
    // Start slider transition timer
    this.startSlideTimer();

    // Initialize AOS (Animate on Scroll)
    if (typeof AOS !== 'undefined') {
      AOS.init({
        duration: 800,
        once: true
      });
    }

    // Initialize Dynamic Network connection nodes in the header background
    this.initHeaderParticles();
  }

  ngOnDestroy() {
    if (this.slideIntervalId) clearInterval(this.slideIntervalId);
  }

  initHeaderParticles() {
    const canvas = document.getElementById('headerParticles') as HTMLCanvasElement;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let width = canvas.width = canvas.offsetWidth;
    let height = canvas.height = canvas.offsetHeight;

    window.addEventListener('resize', () => {
      if (canvas) {
        width = canvas.width = canvas.offsetWidth;
        height = canvas.height = canvas.offsetHeight;
      }
    });

    const particles: Array<{ x: number; y: number; vx: number; vy: number; radius: number }> = [];
    const particleCount = 25;

    for (let i = 0; i < particleCount; i++) {
      particles.push({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.4,
        vy: (Math.random() - 0.5) * 0.4,
        radius: Math.random() * 2 + 1
      });
    }

    const animate = () => {
      if (!ctx || !canvas) return;
      ctx.clearRect(0, 0, width, height);

      // Draw thin connecting paths representation
      ctx.strokeStyle = 'rgba(19, 153, 209, 0.12)';
      ctx.lineWidth = 0.8;
      for (let i = 0; i < particleCount; i++) {
        for (let j = i + 1; j < particleCount; j++) {
          const dist = Math.hypot(particles[i].x - particles[j].x, particles[i].y - particles[j].y);
          if (dist < 90) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }

      // Draw glowing particle nodes
      ctx.fillStyle = 'rgba(19, 153, 209, 0.35)';
      for (let i = 0; i < particleCount; i++) {
        const p = particles[i];
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fill();

        p.x += p.vx;
        p.y += p.vy;

        if (p.x < 0 || p.x > width) p.vx *= -1;
        if (p.y < 0 || p.y > height) p.vy *= -1;
      }

      requestAnimationFrame(animate);
    };

    animate();
  }
}
